import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import ScheduleGrid from "../components/ScheduleGrid";
import { apiClient } from "../services/api";
import { ClassData, DEFAULT_SCHEDULE_CONFIG } from "../types/schedule";

function SchedulePage() {
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedWeek, setSelectedWeek] = useState<number>(0); // 0: 이번주, -1: 1주 전 …
  const [teachers, setTeachers] = useState<string[]>([]);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const navigate = useNavigate();
  const hasSetDefaultTeacher = useRef(false);

  const config = DEFAULT_SCHEDULE_CONFIG;

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (classes.length === 0) return;

    const uniqueTeachers = Array.from(new Set(classes.map((c) => c.teacher)));
    setTeachers(uniqueTeachers.sort());

    if (!hasSetDefaultTeacher.current) {
      const token = localStorage.getItem("jwtToken");
      if (token) {
        try {
          const parts = token.split(".");
          if (parts.length === 3) {
            const bytes = Uint8Array.from(atob(parts[1]), (c) => c.charCodeAt(0));
            const decoded = JSON.parse(new TextDecoder().decode(bytes));
            const username = decoded.username;
            if (username && uniqueTeachers.includes(username)) {
              setSelectedTeacher(username);
              hasSetDefaultTeacher.current = true;
            }
          }
        } catch (e) {
          console.error("Failed to decode JWT:", e);
        }
      }
    }
  }, [classes]);

  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
  };

  // 주차 필터
  const isClassInWeek = (cls: ClassData, weekOffset: number): boolean => {
    const today = new Date();
    const monday = new Date(today);
    const day = today.getDay();
    monday.setDate(today.getDate() - day + (day === 0 ? -6 : 1) + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    if (cls.classCategory === "special" && cls.classStartDate && cls.classEndDate) {
      const start = new Date(cls.classStartDate);
      const end = new Date(cls.classEndDate);
      return !(end < monday || start > sunday);
    }
    if (cls.classCategory === "supplement" && cls.classStartDate) {
      const supplementDate = new Date(cls.classStartDate);
      return supplementDate >= monday && supplementDate <= sunday;
    }
    return true; // 정규반
  };

  const filteredByWeek = classes.filter((cls) => isClassInWeek(cls, selectedWeek));

  const filteredClasses = selectedTeacher
    ? filteredByWeek.filter((c) => c.teacher === selectedTeacher)
    : filteredByWeek;

  return (
    <PageLayout title="시간표">
      {/* 필터 컨트롤 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontWeight: 600, color: "#222" }}>교사 선택:</label>
          <select
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
          >
            <option value="">전체 교사</option>
            {teachers.map((teacher) => (
              <option key={teacher} value={teacher}>
                {teacher}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontWeight: 600, color: "#222" }}>주차:</label>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
          >
            <option value={0}>이번주</option>
            <option value={-1}>1주 전</option>
            <option value={-2}>2주 전</option>
            <option value={-3}>3주 전</option>
          </select>
        </div>
      </div>

      <ScheduleGrid
        classes={filteredClasses}
        config={config}
        hoveredKey={hoveredKey}
        setHoveredKey={setHoveredKey}
        onClassClick={(classId) => navigate(`/class/${classId}`)}
      />
    </PageLayout>
  );
}

export default SchedulePage;
